import React from 'react';
import 'ol/ol.css';
import olMap from 'ol/Map';
import PropTypes from 'prop-types';
import TssLaneContainer from 'Common/Map/OpenLayers/Containers/TssLaneContainer';
import { Id, Link, LinkGeometry, OpStatus, Node, Midpoint } from 'NSwag/ServerClient/Client.nswag';
import MultiLineString from 'ol/geom/MultiLineString';
import LineString from 'ol/geom/LineString';
import TssLinkTooltip from './TssLinkTooltip';
import Util from 'Util/index';
import { TooltipProperties } from 'Common/Map/OpenLayers/Common/TooltipProperties';
import { ILanePollData } from 'Store/index';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';

interface IProps {
    link: Link
    opStatus: OpStatus,
    linkGeometry: LinkGeometry | null,
    previousLinkGeometry: LinkGeometry | null,
    nextLinkGeometry: LinkGeometry | null,
    startNode: Node | null,
    endNode: Node | null,
    pollData?: ILanePollData,
    map: olMap,
    vectorLayerSource: VectorSource,
    zoomLevel: number
}

interface IState {
    tooltip: HTMLDivElement | null;
    feature: Feature | null;
    lineContainer: MultiLineString | null;
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class TssLink extends React.Component<IProps, IState> {

    props!: IProps;

    id: Id

    /**
    * @constructor
    * @param { object } props the set of properties associated with this component
    * @return { void }
    */
    constructor(props: IProps) {
        super(props);
        this.id = props.link.id!;

        this.state = {
            tooltip: null,
            feature: null,
            lineContainer: new MultiLineString([])
        }
    }

    componentDidMount = () => {
        if (this.props.vectorLayerSource) {
            this.addToVectoryLayer();
        }
    }

    /**
     * Method fired before component re-renders to determine if it should re-render or not
     * @returns {boolean} - true if component needs to be re-rendered; false if not.
     */
    shouldComponentUpdate = (nextProps: IProps, nextState: IState) => {
        let retVal = false;
        if (this.props != null && this.props.linkGeometry != nextProps.linkGeometry) {
            retVal = true;
        }

        if (this.props.zoomLevel !== nextProps.zoomLevel) {
            retVal = true;
        }

        return retVal;
    }

    componentDidUpdate = (prevProps: IProps, prevState: IState) => {

        const { link } = this.props;

        if (prevProps.vectorLayerSource != this.props.vectorLayerSource ||
            prevState.lineContainer != this.state.lineContainer) {
            this.addToVectoryLayer();
        }
    }

    addToVectoryLayer = () => {
        const { lineContainer } = this.state;
        if (lineContainer) {
            let feature = new Feature(lineContainer);
            feature.setStyle(new Style({
                stroke: new Stroke({
                    color: '#00000011'
                })
            }));
            this.props.vectorLayerSource.addFeature(feature);
            this.setState({ feature }, this.setTooltipProperties)
        }
    }

    updateFeature = (lineString: LineString) => {
        const { lineContainer } = this.state;
        if (lineContainer) {
            lineContainer.appendLineString(lineString);
        }
    }

    setTooltipProperties = () => {
        const { link, pollData } = this.props;
        const { feature } = this.state;
        if (feature) {
            let tooltipProperties = new TooltipProperties({
                id: Util.IdToString(link.id!),
                element: this.getTooltip
            });

            feature.set("tooltipProperties", tooltipProperties);
        }
    }

    getTooltip = () => {
        const { link, pollData } = this.props;
        return <TssLinkTooltip
            link={link}
            pollData={pollData}
        />
    }

    /**
    * Define how the component will render (html elements or other components)
    * @return { null } returns null to indicate that it does not render
    */
    render() {

        const { link, previousLinkGeometry, opStatus, nextLinkGeometry, startNode,
            endNode, linkGeometry, map, vectorLayerSource, zoomLevel } = this.props;

        if (linkGeometry == null) {
            return null;
        }

        let previousMidPoint: Midpoint | null = null;
        if (previousLinkGeometry != null && previousLinkGeometry.midpoints) {
            let midPointCount = previousLinkGeometry.midpoints.length;
            previousMidPoint = previousLinkGeometry.midpoints[midPointCount - 1];
        }

        let nextMidPoint: Midpoint | null = null;
        if (nextLinkGeometry != null && nextLinkGeometry.midpoints) {
            nextMidPoint = nextLinkGeometry.midpoints[0];
        }

        return (
            <div>
                {
                    link!.lanes!.map((lane, index) =>
                        <TssLaneContainer
                            key={index}
                            index={index}
                            opStatus={opStatus}
                            speedLimit={link.speedLimit}
                            previousMidPoint={previousMidPoint}
                            nextMidPoint={nextMidPoint}
                            startNode={startNode}
                            endNode={endNode}
                            linkGeometry={linkGeometry}
                            lane={lane}
                            map={map}
                            vectorLayerSource={vectorLayerSource}
                            zoomLevel={zoomLevel}
                            onLineStringAdded={this.updateFeature}
                        />
                    )
                }
            </div>
        );
    }
}

/*
 * Make our component the default export object and attach the styles defined
 */
export default TssLink